<template>
  <div
    class="flex items-center flex-wrap"
    ref="containerRef"
    style="gap: 0.5rem"
    v-tooltip.bottom="
      hiddenCount > 0 && {
        value: tooltipValue,
        escape: true,
        class: 'list-cell-tooltip',
      }
    "
  >
    <Tag v-for="chip in getChips">{{ chip }}</Tag>
    <div v-if="hiddenCount > 0 && !isResizing">+{{ hiddenCount }}</div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, computed, watch, type PropType, nextTick } from 'vue';
import { useElementSize } from '@vueuse/core';
import { debounce } from 'lodash-es';
import { Tag } from '../../../../misc/tags/tag';
const props = defineProps({
  chips: {
    type: Array as PropType<string[] | undefined>,
    default: [],
  },
});

const containerRef = ref<HTMLElement | null>(null);

const { width: containerWidth } = useElementSize(containerRef);

const visibleChips = ref<string[]>([]);
const hiddenCount = ref(1);
const isResizing = ref(false);

const tooltipValue = computed(() => {
  return props.chips?.toString()?.replace(/,(\S)/g, ', $1'); // Comma's with ', '
});

const getChips = computed(() => {
  return visibleChips.value.length > 0 ? visibleChips.value : props.chips;
});

const calculateVisibleChips = async () => {
  isResizing.value = false;

  const children = containerRef.value?.children;
  let maxVisibleChildren = 1;
  let totalWidth = 27; // Largest Width of +N

  if (children) {
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      if (!child.classList.contains('p-tag')) {
        break;
      }
      const childMargin = window.getComputedStyle(child)?.marginRight || '0';
      const childWidth = child.offsetWidth + parseInt(childMargin);

      totalWidth += childWidth;
      if (totalWidth > containerWidth.value) {
        break;
      }
      maxVisibleChildren++;
    }
  }

  visibleChips.value = props.chips.slice(0, maxVisibleChildren);
  hiddenCount.value = props.chips.length - maxVisibleChildren;
};

const executeChipCalculation = debounce(async () => {
  visibleChips.value = [];
  await nextTick();
  calculateVisibleChips();
}, 100);

watch(containerWidth, async (newVal, oldVal) => {
  if (newVal && oldVal) {
    isResizing.value = true;
    executeChipCalculation();
  }
});

watch(
  () => props.chips,
  async (newVal, oldVal) => {
    if (newVal && oldVal) {
      visibleChips.value = [];
      hiddenCount.value = 1;
      await nextTick();
      calculateVisibleChips();
    }
  },
  { immediate: false },
);

onMounted(() => {
  calculateVisibleChips();
});
</script>

<style lang="scss" scoped></style>
